import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "panel-stake" }
const _hoisted_2 = { class: "flex flex-column sm:flex-row justify-content-between" }
const _hoisted_3 = { class: "flex sm:flex-order-2 sm:ml-auto align-items-center mb-s3 sm:mb-s0" }
const _hoisted_4 = { class: "sm:flex mr-s2 align-items-center" }
const _hoisted_5 = { class: "mt-s1" }
const _hoisted_6 = { class: "text-muted ml-s1" }
const _hoisted_7 = { class: "flex align-items-center sm:ml-s2 mt-s1" }
const _hoisted_8 = { class: "flex flex-column sm:flex-row" }
const _hoisted_9 = { class: "w-full sm:w-8 mt-s3" }
const _hoisted_10 = { class: "w-full sm:w-4 mt-s3 sm:pl-s3 align-self-center" }

import { computed, nextTick, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { toWei } from '@/sdk/utils';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { STEP_STATUS } from '@/components/stepper/stepper.types';
import { printUSD } from '@/utils/format';
import { Pair } from '@/sdk/entities/pair';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import { FarmSourceType } from '@/sdk/entities/farmSource';
import { Token } from '@/sdk/entities/token';
import FilledListItem from '@/components/filledList/FilledListItem.vue';
import InputPanel from './InputPanel.vue';
import StakeConfirmation from '../confirmation/StakeConfirmation.vue';
import Caption from '@/components/Caption.vue';
import Spinner from '@/components/Spinner.vue';
import TooltipIcon from '@/components/tooltip/TooltipIcon.vue';
import LpAmountFormat from '@/components/LpAmountFormat.vue';
import { useWallet } from '@/store/modules/wallet/useWallet';
import { useFarmingSteps } from '@/store/modules/farming/useFarmingSteps';
import { useFarmingStake } from '@/store/modules/farming/stake/useFarmingStake';
import { useFarmingStakeSteps } from '@/store/modules/farming/stake/useFarmingStakeSteps';
import { useFarmingUnstake } from '@/store/modules/farming/unstake/useFarmingUnstake';
import { useFarmingUnstakeSteps } from '@/store/modules/farming/unstake/useFarmingUnstakeSteps';
import { useAllSteps } from '@/composables/useAllSteps';
import { useMilkomedaWSCBalanceADAIntoCardanoValidator } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBalanceADAIntoCardanoValidator';
import { checkIfNetworkIdDisabledToAdd } from '@/helpers/network-disable.helper';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'Stake',
  props: {
    farm: null,
    tokens: null,
    wscAdaValidator: null
  },
  emits: ["sortFarms", "setTabActive", "sendTransaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'sortFarms'): void;
  (e: 'setTabActive', tab: string): void;
  (e: 'sendTransaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  farm: PortfolioFarm;
  tokens: Pair | Token[];
  wscAdaValidator: ReturnType<typeof useMilkomedaWSCBalanceADAIntoCardanoValidator>;
}





const { t } = useI18n();
const { state } = useStore();
const { walletState } = useWallet();
const { farmingSteps, setFarmingAction, $reset: resetFarmingSteps } = useFarmingSteps();
const {
  farmingStakeForm,
  checkStakeForBridgeFromCardano,
  setStakeToFarm,
  checkAllowance,
  setAllowance,
  doStake,
  $reset: resetFarmingStake,
} = useFarmingStake();
const { farmingStakeSteps } = useFarmingStakeSteps();
const {
  farmingUnstakeForm,
  checkUnstakeForBridgeFromMilkomeda,
  setUnstakeFromFarm,
  doUnstake,
  $reset: resetFarmingUnstake,
} = useFarmingUnstake();
const { farmingUnstakeSteps } = useFarmingUnstakeSteps();
const allStepsState = useAllSteps();

const tabActive = ref<'stake' | 'unstake'>('stake');
const isShowDialog = ref(false);
const inputStakeValue = ref('');
const inputUnstakeValue = ref('');
const disableStake = ref(true);
const disableUnstake = ref(true);
const inProgress = ref(false);
const isResetInput = ref(false);
const isValidCardanoBalanceRef = toRef(
  props.wscAdaValidator.milkomedaWSCBalanceADAIntoCardanoValidatorState,
  'isValidCardanoBalance',
);

const isNotEnoughADA = computed(
  () => ENABLE_FAKE_CARDANO_NETWORK && !isValidCardanoBalanceRef.value,
);
const isStakeTabActive = computed(() => {
  return tabActive.value === 'stake';
});
const isNetworkDisabledToAdd = computed(
  () => checkIfNetworkIdDisabledToAdd(DEFAULT_NETWORK_ID) && isStakeTabActive.value,
);
const activeTabInputValue = computed(() => {
  return isStakeTabActive.value ? inputStakeValue.value : inputUnstakeValue.value;
});
const stakeAmountToken = computed(() => {
  return {
    amount: toWei(activeTabInputValue.value ?? '0', props.farm.liquidityToken.decimals).toString(),
    token: props.farm.liquidityToken,
  };
});
const inputInUSD = computed(() => {
  return props.farm.calculateUSD(activeTabInputValue.value);
});
const isUnstakeTabActive = computed(() => {
  return tabActive.value === 'unstake';
});
const isDisabledInput = computed(() => {
  return !walletState.isInjected || inProgress.value || allStepsState.isInProgress.state;
});
const isDisabledButton = computed(() => {
  const disableButton = isStakeTabActive.value ? disableStake.value : disableUnstake.value;
  return disableButton || isDisabledInput.value || isNotEnoughADA.value;
});
const isSuccessfulStakeSteps = computed(() => {
  if (!farmingStakeSteps.steps.length) return false;
  if (!farmingStakeForm.farm) return false;
  if (farmingSteps.action !== 'stake') return false;

  return (
    !farmingSteps.inProgress &&
    farmingStakeForm.farm.farm === props.farm.farm &&
    farmingStakeSteps.steps.at(-1)?.status === STEP_STATUS.SUCCESS
  );
});
const isSuccessfulUnstakeSteps = computed(() => {
  if (!farmingUnstakeSteps.steps.length) return false;
  if (!farmingUnstakeForm.farm) return false;
  if (farmingSteps.action !== 'unstake') return false;

  return (
    !farmingSteps.inProgress &&
    farmingUnstakeForm.farm.farm === props.farm.farm &&
    farmingUnstakeSteps.steps.at(-1)?.status === STEP_STATUS.SUCCESS
  );
});
const isPairFarm = computed(() => {
  return props.farm.sourceType === FarmSourceType.PAIR;
});
const getFarmName = computed(() => {
  return isPairFarm.value
    ? (props.tokens as Pair).token0.symbol + ' + ' + (props.tokens as Pair).token1.symbol
    : props.farm.portfolio.name;
});

function resetForm() {
  emits('sortFarms');
  onChangeStakeInput('');
  onChangeUnstakeInput('');
  isResetInput.value = true;
}

function setTabActive(tab) {
  emits('setTabActive', tab);
  tabActive.value = tab;

  checkBalanceADAIntoCardano();
}

function onDialogOpen() {
  isShowDialog.value = true;
}

function onDialogClose() {
  isShowDialog.value = false;
}

// CONFIRM STAKE / UNSTAKE
async function onConfirmWSCAction() {
  if (farmingSteps.isShown) {
    resetFarmingSteps();
  }

  setFarmingAction(tabActive.value);

  const { amount: amountInWei, token } = stakeAmountToken.value;

  if (isStakeTabActive.value) {
    checkStakeForBridgeFromCardano(amountInWei, token);
    setStakeToFarm(props.farm, amountInWei);
    await checkAllowance();
  } else if (isUnstakeTabActive.value) {
    checkUnstakeForBridgeFromMilkomeda(amountInWei, token);
    setUnstakeFromFarm(props.farm, amountInWei);
  }

  await nextTick();

  emits('sendTransaction');
}

async function onConfirmAction() {
  inProgress.value = true;

  const amountInInWei = toWei(
    activeTabInputValue.value ?? '0',
    props.farm.liquidityToken.decimals,
  ).toString();

  try {
    if (isStakeTabActive.value) {
      setStakeToFarm(props.farm, amountInInWei);

      await checkAllowance();
      if (!farmingStakeForm.hasAllowance) {
        await setAllowance();
      }
      await doStake();
    } else if (isUnstakeTabActive.value) {
      setUnstakeFromFarm(props.farm, amountInInWei);
      await doUnstake();
    }

    resetForm();
  } finally {
    inProgress.value = false;
  }
}

async function onConfirm() {
  onDialogClose();

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    await onConfirmWSCAction();
    return;
  }

  await onConfirmAction();
}

function onChangeStakeInput(changedInputValue) {
  inputStakeValue.value = changedInputValue;
}

function onChangeUnstakeInput(changedInputValue) {
  inputUnstakeValue.value = changedInputValue;
}

function onAfterReset() {
  isResetInput.value = false;
}

function checkBalanceADAIntoCardano() {
  if (!walletState.isInjected) return;

  const checkBridgeFromMilkomeda = () => {
    const { amount: amountInWei, token } = stakeAmountToken.value;
    props.wscAdaValidator.checkBalanceADAForBridgeFromMilkomeda(amountInWei, token);
  };

  if (isStakeTabActive.value && !inputStakeValue.value) {
    checkBridgeFromMilkomeda();

    return;
  }

  if (isStakeTabActive.value) {
    const { amount: amountInWei, token } = stakeAmountToken.value;
    props.wscAdaValidator.checkBalanceADAForBridgeFromCardano(amountInWei, token);

    return;
  }

  if (isUnstakeTabActive.value) {
    checkBridgeFromMilkomeda();

    return;
  }
}

watch(inputStakeValue, amount => {
  checkBalanceADAIntoCardano();

  if (!amount) {
    resetFarmingStake();
    return;
  }
});

watch(inputUnstakeValue, amount => {
  checkBalanceADAIntoCardano();

  if (!amount) {
    resetFarmingUnstake();
    return;
  }
});

watch(isSuccessfulStakeSteps, isSuccessful => {
  if (isSuccessful) {
    resetForm();
  }
});

watch(isSuccessfulUnstakeSteps, isSuccessful => {
  if (isSuccessful) {
    resetForm();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_Button, {
          label: _unref(t)('stake'),
          class: _normalizeClass(["w-6 button-base mr-s1", { active: _unref(isStakeTabActive) }]),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (setTabActive('stake')))
        }, null, 8, ["label", "class"]),
        _createVNode(_component_Button, {
          label: _unref(t)('unstake'),
          class: _normalizeClass(["w-6 button-base ml-s1", { active: _unref(isUnstakeTabActive) }]),
          onClick: _cache[1] || (_cache[1] = ($event: any) => (setTabActive('unstake')))
        }, null, 8, ["label", "class"])
      ]),
      _createElementVNode("div", null, [
        _createVNode(Caption, {
          caption: _unref(t)('farming.youStaked.caption'),
          tooltip: _unref(t)('farming.youStaked.tooltip'),
          class: "mb-s1"
        }, null, 8, ["caption", "tooltip"]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(LpAmountFormat, {
              "lp-amount": __props.farm.printDeposited.toExact()
            }, null, 8, ["lp-amount"]),
            _createElementVNode("span", _hoisted_6, " / " + _toDisplayString(_unref(printUSD)(__props.farm.printDepositedUSD)), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_Button, {
              class: "text-sm button-text",
              label: _unref(t)('getLpTokens'),
              onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$router.push(`/portfolios/${__props.farm.portfolio.contractAddress}`)))
            }, null, 8, ["label"]),
            _createVNode(TooltipIcon, {
              tooltip: _unref(t)('tooltip.getLpTokens')
            }, null, 8, ["tooltip"])
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, [
        _withDirectives(_createVNode(InputPanel, {
          action: "stake",
          tokens: (__props.tokens as Pair),
          farm: __props.farm,
          onHasValidationError: _cache[3] || (_cache[3] = ($event: any) => (disableStake.value = $event.hasError)),
          onInputChanged: onChangeStakeInput,
          onAfterReset: onAfterReset,
          inputValue: inputStakeValue.value,
          isResetInput: isResetInput.value,
          disabled: _unref(isDisabledInput)
        }, null, 8, ["tokens", "farm", "inputValue", "isResetInput", "disabled"]), [
          [_vShow, _unref(isStakeTabActive)]
        ]),
        _withDirectives(_createVNode(InputPanel, {
          action: "unstake",
          tokens: (__props.tokens as Pair),
          farm: __props.farm,
          onHasValidationError: _cache[4] || (_cache[4] = ($event: any) => (disableUnstake.value = $event.hasError)),
          onInputChanged: onChangeUnstakeInput,
          onAfterReset: onAfterReset,
          inputValue: inputUnstakeValue.value,
          isResetInput: isResetInput.value,
          disabled: _unref(isDisabledInput)
        }, null, 8, ["tokens", "farm", "inputValue", "isResetInput", "disabled"]), [
          [_vShow, _unref(isUnstakeTabActive)]
        ])
      ]),
      _createElementVNode("div", _hoisted_10, [
        _createVNode(FilledListItem, {
          caption: _unref(t)('inUsd'),
          data: `${_unref(printUSD)(_unref(inputInUSD))}`
        }, null, 8, ["caption", "data"]),
        _createVNode(_component_Button, {
          class: "w-full button-primary mt-s3 button-lg",
          onClick: _cache[5] || (_cache[5] = ($event: any) => (onDialogOpen())),
          disabled: _unref(isDisabledButton) || _unref(isNetworkDisabledToAdd)
        }, {
          default: _withCtx(() => [
            (inProgress.value)
              ? (_openBlock(), _createBlock(Spinner, {
                  key: 0,
                  color: "white",
                  size: "24",
                  class: "mr-s2"
                }))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(inProgress.value ? _unref(t)('inProgress') : _unref(t)(tabActive.value)), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ])
    ]),
    _createVNode(_component_Dialog, {
      visible: isShowDialog.value,
      "onUpdate:visible": _cache[6] || (_cache[6] = ($event: any) => ((isShowDialog).value = $event)),
      header: _unref(t)(`farming.${tabActive.value}ConfirmDialog.header`, { farmName: _unref(getFarmName) }),
      dismissableMask: "",
      modal: true,
      "content-class": _unref(state).layout.isMobile ? 'scroll-container' : ''
    }, {
      default: _withCtx(() => [
        _createVNode(StakeConfirmation, {
          action: tabActive.value,
          stake: _unref(activeTabInputValue),
          stakeUsd: _unref(inputInUSD).toString(),
          "in-progress": inProgress.value,
          cancel: onDialogClose,
          confirm: onConfirm
        }, null, 8, ["action", "stake", "stakeUsd", "in-progress"])
      ]),
      _: 1
    }, 8, ["visible", "header", "content-class"])
  ]))
}
}

})